/** Time-boxed identity de-dupe for inbox alerts. */

const TTL_MS = 30 * 60 * 1000;
const MAX_ENTRIES = 2000;
export const FALLBACK_DEDUPE_MS = 750;

/** @type {Map<string, number>} */
const seenEvents = new Map();

/**
 * @param {string} key stable event id, e.g. `msg-1234`
 * @param {number} [nowMs]
 * @returns {boolean} true when this key has not been alerted recently
 */
export function shouldAlertOnce(key, nowMs = Date.now(), ttlMs = TTL_MS) {
  if (!key) return true;
  const alertedAt = seenEvents.get(key);
  if (alertedAt != null && nowMs - alertedAt < ttlMs) return false;
  seenEvents.set(key, nowMs);
  prune(seenEvents, MAX_ENTRIES, TTL_MS, nowMs);
  return true;
}

/**
 * Deterministic, non-cryptographic fingerprint used only when an event has no
 * durable id. Its short TTL collapses immediate multi-room socket copies without
 * hiding two genuine identified messages that arrive rapidly.
 */
export function createInboxAlertFingerprint(parts = []) {
  const source = parts.map((part) => String(part ?? "").trim()).join("\u001f");
  let hash = 2166136261;
  for (let index = 0; index < source.length; index += 1) {
    hash ^= source.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `fp-${(hash >>> 0).toString(36)}`;
}

/**
 * @param {Map<string, number>} entries
 * @param {number} maxEntries
 * @param {number} ttlMs
 * @param {number} nowMs
 */
function prune(entries, maxEntries, ttlMs, nowMs) {
  if (entries.size <= maxEntries) return;
  for (const [key, alertedAt] of entries) {
    if (nowMs - alertedAt >= ttlMs) entries.delete(key);
  }
  while (entries.size > maxEntries) {
    const oldest = entries.keys().next();
    if (oldest.done) break;
    entries.delete(oldest.value);
  }
}

export function resetInboxAlertDedupe() {
  seenEvents.clear();
}
